import { App } from "antd";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { FC, useCallback, useEffect, useState } from "react";

import { useAppDispatch, useOfflineModeSelector } from "@/hooks/redux";
import { BlockLoader } from "@/shared/ui/BlockLoader";
import { PrimaryText } from "@/shared/ui/Typography";

dayjs.extend(isBetween);

import {
    getApiError,
    getInvoiceInvoiceWithProductsInfoCollection,
    getInvoicePaymentsListByConsignmentsIds,
    getMarketGetShipmentRangeDateByPoint,
    getMarketShipmentsByPoint,
} from "tm-shared/api";
import { backendDateFormat } from "tm-shared/constants";
import {
    DateRange,
    InvoiceDetails,
    MarketShipmentPayment,
    ShipmentView,
} from "tm-shared/types";

import { ShipmentAccordions } from "./ShipmentAccordions";
import { ShipmentRangePicker } from "./ShipmentRangePicker";

interface PointShipmentHistoryProps {
    pointId: string;
    initialDate?: string;
}

interface ShipmentData {
    period: DateRange | null;
    shipments: ShipmentView[];
    invoices: InvoiceDetails[];
    payments: { [consignmentId: string]: MarketShipmentPayment[] };
}

export const PointShipmentHistory: FC<PointShipmentHistoryProps> = ({
    pointId,
    initialDate,
}) => {
    const { notification } = App.useApp();
    const offlineMode = useOfflineModeSelector();

    const dispatch = useAppDispatch();

    const [data, setData] = useState<ShipmentData>({
        period: null,
        shipments: [],
        invoices: [],
        payments: {},
    });

    const [datesConfirmed, setDatesConfirmed] = useState<null | DateRange>(
        null,
    );

    const [loading, setLoading] = useState(false);
    const [loadingShipment, setLoadingShipment] = useState(false);

    const handleSetDatesConfirmed = useCallback((value: DateRange) => {
        setDatesConfirmed((prev) => {
            if (
                prev?.startDate === value.startDate &&
                prev?.endDate === value.endDate
            ) {
                return prev;
            }
            return value;
        });
    }, []);

    useEffect(() => {
        if (offlineMode) return;

        const fetchPeriod = async () => {
            try {
                setLoadingShipment(true);

                const { result: period } =
                    await getMarketGetShipmentRangeDateByPoint({
                        pointId,
                    });

                setData((prev) => ({ ...prev, period }));

                const initialDayjs = dayjs(initialDate);

                if (
                    initialDate &&
                    initialDayjs.isBetween(
                        period.startDate,
                        period.endDate,
                        "day",
                        "[]",
                    )
                ) {
                    const formatInitialDate = initialDayjs.format(
                        backendDateFormat.date,
                    );

                    handleSetDatesConfirmed({
                        startDate: formatInitialDate,
                        endDate: formatInitialDate,
                    });
                }
            } catch (error) {
                const { displayMessage } = getApiError(error);
                notification.error({
                    title: "Не вдалося отримати період",
                    description: displayMessage,
                });
            } finally {
                setLoadingShipment(false);
            }
        };

        fetchPeriod();
    }, [
        notification,
        pointId,
        dispatch,
        offlineMode,
        initialDate,
        handleSetDatesConfirmed,
    ]);

    useEffect(() => {
        const fetchHistory = async () => {
            if (!datesConfirmed?.startDate || !datesConfirmed?.endDate) return;

            setLoading(true);
            try {
                const { result: shipments } = await getMarketShipmentsByPoint({
                    pointId,
                    ...datesConfirmed,
                });

                const invoiceIds = shipments
                    .map((s) => s.invoiceId)
                    .filter((id): id is string => Boolean(id));
                const consignmentsIds = shipments.map((s) => s.consignmentId);

                const [invoicesResult, paymentsResult] = await Promise.all([
                    invoiceIds.length > 0
                        ? getInvoiceInvoiceWithProductsInfoCollection({
                              invoiceIds,
                          })
                        : Promise.resolve(null),
                    consignmentsIds.length > 0
                        ? getInvoicePaymentsListByConsignmentsIds({
                              consignmentsIds,
                          })
                        : Promise.resolve(null),
                ]);

                setData((prev) => ({
                    ...prev,
                    shipments,
                    invoices: invoicesResult?.result ?? [],
                    payments: paymentsResult?.result ?? {},
                }));
            } catch (error) {
                const { displayMessage } = getApiError(error);
                notification.error({
                    title: "Не вдалося завантажити історію",
                    description: displayMessage,
                });
            } finally {
                setLoading(false);
            }
        };

        void fetchHistory();
    }, [notification, pointId, dispatch, datesConfirmed]);

    if (loadingShipment) return <BlockLoader />;

    if (!data.period?.startDate || !data.period?.endDate) {
        return (
            <PrimaryText fontSize={16} textAlign="center">
                За даною ТТ історія продажів не знайдена
            </PrimaryText>
        );
    }

    return (
        <>
            <ShipmentRangePicker
                limits={data.period}
                setDatesConfirmed={handleSetDatesConfirmed}
                disabled={offlineMode || loading}
                datesConfirmed={datesConfirmed}
            />

            {loading ? (
                <BlockLoader />
            ) : (
                <ShipmentAccordions
                    shipments={data.shipments}
                    invoices={data.invoices}
                    payments={data.payments}
                />
            )}
        </>
    );
};
